import {
  Avatar,
  AvatarProps,
  Dialog,
  Grid,
  Icon,
  IconButton,
  Snackbar,
  Typography
} from '@material-ui/core'
import IconCloseMenu from '@material-ui/icons/Close'
import IconAlerts from '@material-ui/icons/ErrorOutlineOutlined'
import IconArrowDown from '@material-ui/icons/KeyboardArrowDown'
import IconQuickAdd from '@material-ui/icons/Language'
import IconMenu from '@material-ui/icons/Menu'
import IconNotifications from '@material-ui/icons/NotificationsOutlined'
import IconPerson from '@material-ui/icons/Person'
import IconSend from '@material-ui/icons/Send'
import IconKey from '@material-ui/icons/VpnKey'
import { Alert } from '@material-ui/lab'
import React, { ChangeEvent, ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Switch } from 'react-router-dom'
import iconCodeBar from '../../../assets/icons/iconosbtn_barcode.png'
import iconBattery from '../../../assets/icons/iconosbtn_battery_01.png'
import iconSearch from '../../../assets/icons/iconosbtn_diapo-01.png'
import iconHome from '../../../assets/icons/iconosbtn_diapo-02.png'
import iconAddSite from '../../../assets/icons/iconosbtn_diapo-03.png'
import iconFollowProcess from '../../../assets/icons/iconosbtn_diapo-04.png'
import iconSiteVisits from '../../../assets/icons/iconosbtn_diapo-05.png'
import iconBusiness from '../../../assets/icons/iconosbtn_diapo-06.png'
import iconMaintenance from '../../../assets/icons/iconosbtn_diapo-07.png'
import iconWorkOrder from '../../../assets/icons/iconosbtn_diapo-08.png'
import iconReport from '../../../assets/icons/iconosbtn_diapo-09.png'
import iconSettings from '../../../assets/icons/iconosbtn_diapo-10.png'
import iconWorkFlow from '../../../assets/icons/iconosbtn_diapo-workflow.png'
import iconPencil from '../../../assets/icons/pencilicon.svg'
import iconDriveTest from '../../../assets/images/dt_control.svg'
import userLogo from '../../../assets/images/user.png'
import logo_img from '../../../assets/images/vector_logo2.png'
import AppAutocomplete from '../../../components/atoms/AppAutocomplete'
import AppBar from '../../../components/atoms/AppBar'
import AppHeaderMenuItem, {
  AppHeaderMenuItemProps
} from '../../../components/molecules/AppHeaderMenuItem'
import AppLoader from '../../../components/molecules/AppLoader'
import SideBar, {
  SideBarMenuItemProps
} from '../../../components/molecules/AppSideBar'
import AppHeaderTemplate from '../../../components/templates/AppHeader'
import AppHomeTemplate from '../../../components/templates/AppHome'
import { APP_MODULES, PAGE_URL } from '../../../constants'
import { ENABLE_NEW_SITE_VISIT } from '../../../constants/env'
import { navigate, redirectToBackendApp } from '../../../router/redirections'
import { RouteAppComponentProps } from '../../../router/routes'
import { classes } from '../../../utils/css'
import { useScreenDimensions } from '../../../utils/hook'
import AppErrorPage from '../../AppErrorPage'
import AppNotFoundPage from '../../AppNotFoundPage'
import styles from './AppHome.module.css'

type AppHeaderMenuProps = AppHeaderMenuItemProps & {
  avatar?: AvatarProps
  menuName?: string
}

type AppHomeViewProps = RouteAppComponentProps & {
  alertMenu: AppHeaderMenuProps
  notificationMenu: AppHeaderMenuProps
  userMenu: AppHeaderMenuProps
  enableDashboardFrontEnd?: boolean
  enableBatteryManagementFrontEnd?: boolean
  showProjects?: boolean
  projectMenu: AppHeaderMenuItemProps
  projectSelected: string
  searchOptions?: string[]
  onLogoClick?: () => void
  onAutocomplete?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  onSearch?: () => void
  onSearchOptionSelected?: (event: number) => void
  onSelectedMenu?: (event: string) => void
  snackbar?: {
    open: boolean
    onClose?: (event?: React.SyntheticEvent, reason?: string) => void
    body?: ReactElement<any, any>
    duration?: number
    severity: 'error' | 'warning' | 'info' | 'success'
  }
  isLoading?: boolean
  isNotFound?: boolean
  isInternalError?: boolean
  moduleAvailable?: string[]
}

function AppHomeView({
  onLogoClick = () => {},
  onAutocomplete = () => {},
  onSearch = () => {},
  searchOptions = [],
  ...props
}: AppHomeViewProps): JSX.Element {
  const { t } = useTranslation()
  const screen = useScreenDimensions()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuOptions: (SideBarMenuItemProps & { disabled?: boolean })[] = [
    {
      label: (
        <div className={styles.searchContainer}>
          <AppAutocomplete
            inputProps={{
              color: 'secondary',
              className: styles.searchInput,
              placeholder: t('Search'),
              onChange: onAutocomplete
            }}
            options={searchOptions}
            asAutocompleted={true}
            onOptionSelected={props.onSearchOptionSelected}
          />
          <IconButton onClick={onSearch}>
            <IconSend
              color='secondary'
              className={styles.iconInputSearchSend}
            />
          </IconButton>
        </div>
      ),
      icon: (
        <img
          src={iconSearch}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      )
    },
    {
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Dashboard')}
        </Typography>
      ),
      icon: (
        <img
          src={iconHome}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: '/',
      onClick:
        (!props.enableDashboardFrontEnd &&
          (() => redirectToBackendApp(window.location)(''))) ||
        undefined,
      subMenu:
        (props.enableDashboardFrontEnd && [
          {
            label: (
              <Typography
                variant='body2'
                color='secondary'
                className={styles.sideBarLabel}
              >
                {t('Dashboard')}
              </Typography>
            ),
            href: '/',
            onClick: () => redirectToBackendApp(window.location)(''),
            icon: (
              <img
                src={iconHome}
                color='secondary'
                className={styles.sideBarItemIcon}
              />
            )
          },
          {
            label: (
              <Typography
                variant='body2'
                color='secondary'
                className={styles.sideBarLabel}
              >
                {`${t('Dashboard')} (WIP)`}
              </Typography>
            ),
            href: '/',
            onClick: () => {
              navigate(props.history)(PAGE_URL.HOME)
            },
            icon: (
              <img
                src={iconHome}
                color='secondary'
                className={styles.sideBarItemIcon}
              />
            )
          }
        ]) ||
        undefined
    },
    {
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Project Workflow')}
        </Typography>
      ),
      icon: (
        <img
          src={iconWorkFlow}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: '/workflow',
      onClick: () => redirectToBackendApp(window.location)('workflow')
    },
    {
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Add Site Information')}
        </Typography>
      ),
      icon: (
        <img
          src={iconAddSite}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      subMenu: [
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Quick Add')}
            </Typography>
          ),
          icon: (
            <IconQuickAdd
              color='secondary'
              className={styles.sideBarMenuItemIcon}
            />
          ),
          href: '/sites/create/quick',
          onClick: () =>
            redirectToBackendApp(window.location)('sites/create/quick')
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Add New Site')}
            </Typography>
          ),
          icon: (
            <IconQuickAdd
              color='secondary'
              className={styles.sideBarMenuItemIcon}
            />
          ),
          href: '/sites/create',
          onClick: () => redirectToBackendApp(window.location)('sites/create')
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Add New Cabinet')}
            </Typography>
          ),
          icon: (
            <Icon
              color='secondary'
              className={classes('icon-wrench', styles.sideBarMenuItemIcon)}
            />
          ),
          href: '/sites/administration/cabinet/create',
          onClick: () =>
            redirectToBackendApp(window.location)(
              'sites/administration/cabinet/create'
            )
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Add New Microwave')}
            </Typography>
          ),
          icon: (
            <Icon
              color='secondary'
              className={classes('icon-wrench', styles.sideBarMenuItemIcon)}
            />
          ),
          href: '/sites/microwave/create',
          onClick: () =>
            redirectToBackendApp(window.location)('sites/microwave/create')
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Add Photos')}
            </Typography>
          ),
          icon: (
            <Icon
              color='secondary'
              className={classes(
                'icon-cloud-upload',
                styles.sideBarMenuItemIcon
              )}
            />
          ),
          href: '/site/update/photo',
          onClick: () =>
            redirectToBackendApp(window.location)('site/update/photo')
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Add 360 Photos')}
            </Typography>
          ),
          icon: (
            <Icon
              color='secondary'
              className={classes(
                'icon-cloud-upload',
                styles.sideBarMenuItemIcon
              )}
            />
          ),
          href: '/site/update/photo360',
          onClick: () =>
            redirectToBackendApp(window.location)('site/update/photo360')
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Import Network Data')}
            </Typography>
          ),
          icon: (
            <Icon
              color='secondary'
              className={classes('icon-plus', styles.sideBarMenuItemIcon)}
            />
          ),
          href: '/sites/create/RND',
          onClick: () =>
            redirectToBackendApp(window.location)('sites/create/RND')
        }
      ]
    },
    {
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Follow Up Process')}
        </Typography>
      ),
      icon: (
        <img
          src={iconFollowProcess}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      subMenu: [
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Sites Disassembly')}
            </Typography>
          ),
          icon: (
            <Icon
              color='secondary'
              className={classes(
                'icon-speedometer',
                styles.sideBarMenuItemIcon
              )}
            />
          ),
          href: '/followup/6',
          onClick: () => redirectToBackendApp(window.location)('followup/6')
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Project Start')}
            </Typography>
          ),
          icon: (
            <Icon
              color='secondary'
              className={classes(
                'icon-speedometer',
                styles.sideBarMenuItemIcon
              )}
            />
          ),
          href: '/followup/5',
          onClick: () => redirectToBackendApp(window.location)('followup/5')
        }
      ]
    },
    {
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Forms')}
        </Typography>
      ),
      icon: (
        <img
          src={iconPencil}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      subMenu: [
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Forms Management')}
            </Typography>
          ),
          icon: (
            <IconQuickAdd
              color='secondary'
              className={styles.sideBarMenuItemIcon}
            />
          ),
          href: '/poll/list',
          onClick: () => navigate(props.history)(PAGE_URL.FORMS_MANAGEMENT)
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('Create New Form')}
            </Typography>
          ),
          icon: (
            <Icon
              color='secondary'
              className={classes('icon-plus', styles.sideBarMenuItemIcon)}
            />
          ),
          href: '/poll/new',
          onClick: () => navigate(props.history)(PAGE_URL.POLL_NEW)
        },
        {
          label: (
            <Typography
              variant='body2'
              color='secondary'
              className={styles.sideBarLabel}
            >
              {t('External Data & Images')}
            </Typography>
          ),
          icon: (
            <IconQuickAdd
              color='secondary'
              className={styles.sideBarMenuItemIcon}
            />
          ),
          href: PAGE_URL.AUDIT_MANAGEMENT_EXTERNAL_DATA,
          onClick: () =>
            navigate(props.history)(PAGE_URL.AUDIT_MANAGEMENT_EXTERNAL_DATA)
        }
      ]
    },
    ...(ENABLE_NEW_SITE_VISIT
      ? [
          {
            disabled: !props.moduleAvailable?.some((module) =>
              module.match(RegExp(APP_MODULES.SITE_VISITS, 'i'))
            ),
            label: (
              <Typography
                variant='body2'
                color='secondary'
                className={styles.sideBarLabel}
              >
                {t('Site Visits')}
              </Typography>
            ),
            icon: (
              <img
                src={iconSiteVisits}
                color='secondary'
                className={styles.sideBarItemIcon}
              />
            ),
            href: PAGE_URL.SITE_VISITS_DASHBOARD,
            onClick: () =>
              navigate(props.history)(PAGE_URL.SITE_VISITS_DASHBOARD)
          }
        ]
      : [
          {
            disabled: !props.moduleAvailable?.some((module) =>
              module.match(RegExp(APP_MODULES.SITE_VISITS, 'i'))
            ),
            label: (
              <Typography
                variant='body2'
                color='secondary'
                className={styles.sideBarLabel}
              >
                {t('Site Visits')}
              </Typography>
            ),
            icon: (
              <img
                src={iconSiteVisits}
                color='secondary'
                className={styles.sideBarItemIcon}
              />
            ),
            href: PAGE_URL.SITE_VISITS_DASHBOARD,
            onClick: () => redirectToBackendApp(location)('sitevisit/dashboard')
          }
        ]),
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.AUDIT_MANAGEMENT, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Audit Management')}
        </Typography>
      ),
      icon: (
        <img
          src={iconBusiness}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: PAGE_URL.AUDIT_MANAGEMENT_LIST,
      onClick: () => navigate(props.history)(PAGE_URL.AUDIT_MANAGEMENT_LIST)
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.DRIVE_TEST, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Drive Test Control')}
        </Typography>
      ),
      icon: (
        <img
          src={iconDriveTest}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: PAGE_URL.DRIVE_TEST_CONTROL_DASHBOARD,
      onClick: () =>
        navigate(props.history)(PAGE_URL.DRIVE_TEST_CONTROL_DASHBOARD)
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.INCIDENCE, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Incidents')}
        </Typography>
      ),
      icon: (
        <img
          src={iconBusiness}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: '/incidence/list',
      onClick: () => redirectToBackendApp(window.location)('incidence/list')
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.SITE_ACCEPTANCE, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Site Acceptance')}
        </Typography>
      ),
      icon: (
        <img
          src={iconBusiness}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: '/site/acceptance/summary',
      onClick: () =>
        redirectToBackendApp(window.location)('site/acceptance/summary')
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.INVENTORY, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Inventory')}
        </Typography>
      ),
      icon: (
        <img
          src={iconCodeBar}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: '/model/check-availability',
      onClick: () =>
        redirectToBackendApp(window.location)('model/check-availability')
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.BATTERIES, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {`${t('Batteries')}`}
        </Typography>
      ),
      icon: (
        <img
          src={iconBattery}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: PAGE_URL.BATTERIES_MANAGEMENT_DASHBOARD,
      onClick: () =>
        navigate(props.history)(PAGE_URL.BATTERIES_MANAGEMENT_DASHBOARD)
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.MAINTENANCE, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Maintenance')}
        </Typography>
      ),
      icon: (
        <img
          src={iconMaintenance}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: '/maintenance/dashboard',
      onClick: () =>
        redirectToBackendApp(window.location)('maintenance/dashboard')
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.WORK_ORDER, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Work Order')}
        </Typography>
      ),
      icon: (
        <img
          src={iconWorkOrder}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: '/pendingissues/list',
      onClick: () => redirectToBackendApp(window.location)('pendingissues/list')
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.REPORTS, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Reports')}
        </Typography>
      ),
      icon: (
        <img
          src={iconReport}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: '/reports/reports',
      onClick: () => redirectToBackendApp(window.location)('reports/reports')
    },
    {
      disabled: !props.moduleAvailable?.some((module) =>
        module.match(RegExp(APP_MODULES.SETTINGS, 'i'))
      ),
      label: (
        <Typography
          variant='body2'
          color='secondary'
          className={styles.sideBarLabel}
        >
          {t('Settings')}
        </Typography>
      ),
      icon: (
        <img
          src={iconSettings}
          color='secondary'
          className={styles.sideBarItemIcon}
        />
      ),
      href: PAGE_URL.SETTINGS,
      onClick: () => navigate(props.history)(PAGE_URL.SETTINGS)
    }
  ].concat(
    screen.sm
      ? [
          {
            label: (
              <Typography
                variant='body2'
                color='secondary'
                className={styles.sideBarLabel}
              >
                {t('Account')}
              </Typography>
            ),
            icon: (
              <img
                className={styles.sideBarItemIcon}
                src={userLogo}
                color='secondary'
              />
            ),
            subMenu: [
              {
                label: (
                  <Typography
                    variant='body2'
                    color='secondary'
                    className={styles.sideBarLabel}
                  >
                    {t('My Profile')}
                  </Typography>
                ),
                icon: (
                  <IconPerson
                    className={styles.menuOptionsIcon}
                    color='secondary'
                    fontSize='small'
                  />
                ),
                href: PAGE_URL.MY_PROFILE,
                onClick: () =>
                  redirectToBackendApp(window.location)('accounts/profile')
              },
              {
                label: (
                  <Typography
                    variant='body2'
                    color='secondary'
                    className={styles.sideBarLabel}
                  >
                    {t('Logout')}
                  </Typography>
                ),
                icon: (
                  <IconKey
                    className={styles.menuOptionsIcon}
                    color='secondary'
                    fontSize='small'
                  />
                ),
                href: PAGE_URL.LOGOUT,
                onClick: () =>
                  redirectToBackendApp(window.location)('accounts/logout')
              }
            ]
          }
        ]
      : []
  )
  return (
    <>
      <AppHomeTemplate
        isMobile={screen.sm}
        header={
          <AppBar position='fixed'>
            <AppHeaderTemplate
              pl={16}
              pr={16}
              left={
                <Grid container alignItems='stretch'>
                  <Grid item>
                    <a onClick={() => onLogoClick()}>
                      <img
                        className={styles.logo}
                        src={logo_img}
                        alt='logo_sitehub'
                      />
                    </a>
                  </Grid>
                  {props.showProjects && !screen.sm && (
                    <Grid item className={styles.projectSelectorContainer}>
                      <AppHeaderMenuItem
                        {...props.projectMenu}
                        buttonProps={{ 'data-testid': 'select-projects' }}
                        emptyMessage={t('Coming Soon')}
                      >
                        <Grid container justify='space-between'>
                          <Typography
                            variant='body2'
                            color='textSecondary'
                            className={styles.text}
                            gutterBottom
                          >
                            {props.projectSelected || t('All-Projects')}
                          </Typography>
                          <IconArrowDown fontSize='small' color='secondary' />
                        </Grid>
                      </AppHeaderMenuItem>
                    </Grid>
                  )}
                </Grid>
              }
              right={
                (!screen.sm && (
                  <Grid
                    container
                    direction='row'
                    className={styles.headerMenu}
                    alignItems='center'
                    justify='flex-start'
                  >
                    <Grid>
                      <AppHeaderMenuItem
                        {...props.notificationMenu}
                        emptyMessage={t('Coming Soon')}
                      >
                        <Avatar
                          className={classes(
                            styles.avatarWhite,
                            styles.avatar1
                          )}
                          {...props.notificationMenu.avatar}
                        >
                          <IconNotifications fontSize='small' color='primary' />
                        </Avatar>
                      </AppHeaderMenuItem>
                    </Grid>
                    <Grid>
                      <AppHeaderMenuItem
                        {...props.alertMenu}
                        emptyMessage={t('Coming Soon')}
                      >
                        <Avatar
                          className={classes(
                            styles.avatarWhite,
                            styles.avatar1
                          )}
                          {...props.alertMenu.avatar}
                        >
                          <IconAlerts fontSize='small' color='primary' />
                        </Avatar>
                      </AppHeaderMenuItem>
                    </Grid>
                    <Grid>
                      <AppHeaderMenuItem
                        {...props.userMenu}
                        emptyMessage={t('Coming Soon')}
                      >
                        <Avatar
                          className={classes(
                            styles.avatarPrimary,
                            styles.avatar2
                          )}
                        >
                          <img
                            className={styles.userImg}
                            src={userLogo}
                            color='primary'
                          />
                        </Avatar>
                        <Typography
                          variant='body2'
                          color='textSecondary'
                          className={styles.text}
                          gutterBottom
                        >
                          {props.userMenu.menuName}
                        </Typography>
                        <Avatar
                          className={classes(
                            styles.avatarWhite,
                            styles.avatar3
                          )}
                        >
                          <IconArrowDown fontSize='small' color='primary' />
                        </Avatar>
                      </AppHeaderMenuItem>
                    </Grid>
                  </Grid>
                )) || (
                  <Grid
                    container
                    alignItems='center'
                    justify='center'
                    className={classes(
                      styles.menuButton,
                      isMenuOpen ? styles.spin1_8f : styles.spin1_8b
                    )}
                    onClick={() => setIsMenuOpen((current) => !current)}
                  >
                    {isMenuOpen ? (
                      <IconCloseMenu color='secondary' />
                    ) : (
                      <IconMenu color='secondary' />
                    )}
                  </Grid>
                )
              }
            />
          </AppBar>
        }
        menu={
          <SideBar
            isMobile={screen.sm}
            autoClose={!screen.sm}
            stayOpen={isMenuOpen}
            menuOptions={menuOptions.filter(
              (option) => option.disabled === undefined || !option.disabled
            )}
          />
        }
      >
        {props.isNotFound ? (
          <AppNotFoundPage
            pageName='Not Found'
            history={props.history}
            location={props.location}
            match={props.match}
          />
        ) : props.isInternalError ? (
          <AppErrorPage
            pageName='Internal Error'
            history={props.history}
            location={props.location}
            match={props.match}
          />
        ) : (
          <>
            {props.showProjects && screen.sm && (
              <Grid
                container
                className={classes(
                  styles.projectSelectorContainer,
                  styles.mobile
                )}
              >
                <AppHeaderMenuItem
                  {...props.projectMenu}
                  buttonProps={{
                    'data-testid': 'select-projects',
                    className: classes(
                      styles.projectSelectorMenu,
                      styles.mobile
                    )
                  }}
                  badge={{
                    className: styles.fullWidth
                  }}
                  emptyMessage={t('Coming Soon')}
                >
                  <Grid container justify='space-between'>
                    <Typography
                      variant='body2'
                      color='textSecondary'
                      gutterBottom
                    >
                      {props.projectSelected || t('All-Projects')}
                    </Typography>
                    <IconArrowDown fontSize='small' color='secondary' />
                  </Grid>
                </AppHeaderMenuItem>
              </Grid>
            )}
            <Switch>
              {props.routes?.map((route, key) => (
                <Route
                  key={key}
                  path={route.path}
                  exact={route.exact}
                  strict={route.strict}
                  sensitive={route.sensitive}
                >
                  {(routeProps) => (
                    <route.component
                      {...routeProps}
                      routes={route.routes}
                      pageName={route.pageName}
                    />
                  )}
                </Route>
              ))}
            </Switch>
          </>
        )}
      </AppHomeTemplate>
      <Snackbar
        open={props.snackbar?.open}
        onClose={props.snackbar?.onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        className={styles.notification}
        autoHideDuration={props.snackbar?.duration}
      >
        <Alert
          variant='filled'
          severity={props.snackbar?.severity}
          onClose={props.snackbar?.onClose}
        >
          <Grid
            container
            direction='row'
            alignItems='center'
            justify='space-between'
          >
            {props.snackbar?.body}
          </Grid>
        </Alert>
      </Snackbar>
      <Dialog open={!!props.isLoading}>
        <AppLoader />
      </Dialog>
    </>
  )
}

export default AppHomeView
